/**
 * Custom Hooks for Website Hero Settings
 * Provides React hooks for managing website hero section configuration
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { websiteHeroSettingsService } from '../services';
import { WebsiteHeroSetting } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all website hero settings
 */
export function useWebsiteHeroSettings(
  options?: ResourceQueryOptions
): ResourceState<WebsiteHeroSetting> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      websiteHeroSettingsService.getAll(opts) as unknown as Promise<{ data: WebsiteHeroSetting[]; meta?: any }>,
    []
  );
  return useResourceCollection<WebsiteHeroSetting>(fetchFn, options);
}

/**
 * Hook to fetch a single website hero setting by ID
 */
export function useWebsiteHeroSetting(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<WebsiteHeroSetting>((id) => websiteHeroSettingsService.getById(id, options), id);
}

/**
 * Hook to create a new website hero setting
 */
export function useCreateWebsiteHeroSetting(onSuccess?: () => void): MutationState<WebsiteHeroSetting> {
  return useCreateResource<WebsiteHeroSetting>(websiteHeroSettingsService.create, onSuccess);
}

/**
 * Hook to update an existing website hero setting
 */
export function useUpdateWebsiteHeroSetting(onSuccess?: () => void): MutationState<WebsiteHeroSetting> {
  return useUpdateResource<WebsiteHeroSetting>(websiteHeroSettingsService.update, onSuccess);
}

/**
 * Hook to delete a website hero setting
 */
export function useDeleteWebsiteHeroSetting(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(websiteHeroSettingsService.delete, onSuccess);
}

